'use client'

import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { AccessGate } from '@/components/auth/access-gate'
import { cn } from '@/lib/utils'
import { useUserContext } from '@/components/providers'
import { UsePromptFormReturn } from '@/lib/types'

interface PromptAreaDebugProps {
    promptForm: UsePromptFormReturn
}

export function PromptAreaDebug({ promptForm }: PromptAreaDebugProps) {
    const [isOpen, setIsOpen] = useState(false)
    const { userContextValue } = useUserContext()

    // Get comprehensive form state for debugging
    const formState = {
        // Dynamic form values (updates automatically with new fields)
        formValues: promptForm.formValues,
        // Form validation and state
        formErrors: promptForm.form.formState.errors,
        formState: {
            isDirty: promptForm.form.formState.isDirty,
            isValid: promptForm.form.formState.isValid,
            isSubmitting: promptForm.form.formState.isSubmitting,
            isValidating: promptForm.form.formState.isValidating,
            submitCount: promptForm.form.formState.submitCount,
            touchedFields: promptForm.form.formState.touchedFields,
            dirtyFields: promptForm.form.formState.dirtyFields
        }
    }

    const maxWidth = userContextValue?.settings.maxWidth ? `max-w-[${userContextValue.settings.maxWidth}px]` : 'max-w-[800px]'
    return (
        <div className={cn("w-full relative", maxWidth)}>
            {/* Arrow Button */}
            <div className="flex justify-center">
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsOpen(!isOpen)}
                    className="h-6 w-8 p-0 hover:bg-muted/50"
                >
                    {isOpen ? (
                        <ChevronUp className="h-4 w-4" />
                    ) : (
                        <ChevronDown className="h-4 w-4" />
                    )}
                </Button>
            </div>

            {/* Debug Panel */}
            {isOpen && (
                <div className={cn(
                    "w-full border border-yellow-500/50 rounded-lg bg-yellow-50/10 mt-1",
                    "dark:bg-yellow-900/10 dark:border-yellow-400/30"
                )}>
                    <div className="p-3">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="h-2 w-2 bg-yellow-500 rounded-full"></div>
                            <span className="text-xs font-mono text-yellow-600 dark:text-yellow-400">
                                Form debug state for admins
                            </span>
                        </div>

                        <div className="h-[200px] w-full border rounded bg-background/50 overflow-y-scroll overflow-x-auto">
                            <pre className="text-xs font-mono p-3 text-foreground/80 whitespace-pre-wrap break-words">
                                {JSON.stringify(formState, null, 4)}
                            </pre>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
